<template>
  <div class="relative" ref="containerRef">
    <input
      ref="inputRef"
      :value="inputValue"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @keydown="handleKeydown"
      type="text"
      autocomplete="off"
      spellcheck="false"
      class="w-28 px-3 py-2 border border-gray-300 focus:ring-1 focus:ring-gray-900 focus:border-gray-900 text-sm font-mono"
    />
    <div
      v-if="isOpen && filteredOptions.length > 0"
      class="absolute z-50 top-full left-0 w-60 max-h-52 overflow-y-auto bg-white border border-gray-200 shadow-lg"
    >
      <button
        v-for="(option, idx) in filteredOptions"
        :key="option.code"
        type="button"
        @mousedown.prevent="selectOption(option)"
        class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
        :class="{ 'bg-gray-100': idx === activeIndex }"
      >
        <span class="font-mono text-gray-800 w-14 inline-block">{{ option.code }}</span>
        <span class="text-gray-500">{{ option.name }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

const COUNTRY_CODES = [
  { code: '+1', name: 'US / Canada' },
  { code: '+7', name: 'Russia' },
  { code: '+20', name: 'Egypt' },
  { code: '+27', name: 'South Africa' },
  { code: '+30', name: 'Greece' },
  { code: '+31', name: 'Netherlands' },
  { code: '+32', name: 'Belgium' },
  { code: '+33', name: 'France' },
  { code: '+34', name: 'Spain' },
  { code: '+36', name: 'Hungary' },
  { code: '+39', name: 'Italy' },
  { code: '+40', name: 'Romania' },
  { code: '+41', name: 'Switzerland' },
  { code: '+43', name: 'Austria' },
  { code: '+44', name: 'UK' },
  { code: '+45', name: 'Denmark' },
  { code: '+46', name: 'Sweden' },
  { code: '+47', name: 'Norway' },
  { code: '+48', name: 'Poland' },
  { code: '+49', name: 'Germany' },
  { code: '+51', name: 'Peru' },
  { code: '+52', name: 'Mexico' },
  { code: '+54', name: 'Argentina' },
  { code: '+55', name: 'Brazil' },
  { code: '+56', name: 'Chile' },
  { code: '+57', name: 'Colombia' },
  { code: '+60', name: 'Malaysia' },
  { code: '+61', name: 'Australia' },
  { code: '+62', name: 'Indonesia' },
  { code: '+63', name: 'Philippines' },
  { code: '+64', name: 'New Zealand' },
  { code: '+65', name: 'Singapore' },
  { code: '+66', name: 'Thailand' },
  { code: '+81', name: 'Japan' },
  { code: '+82', name: 'South Korea' },
  { code: '+84', name: 'Vietnam' },
  { code: '+86', name: 'China' },
  { code: '+90', name: 'Turkey' },
  { code: '+91', name: 'India' },
  { code: '+92', name: 'Pakistan' },
  { code: '+94', name: 'Sri Lanka' },
  { code: '+95', name: 'Myanmar' },
  { code: '+212', name: 'Morocco' },
  { code: '+213', name: 'Algeria' },
  { code: '+216', name: 'Tunisia' },
  { code: '+234', name: 'Nigeria' },
  { code: '+251', name: 'Ethiopia' },
  { code: '+254', name: 'Kenya' },
  { code: '+255', name: 'Tanzania' },
  { code: '+256', name: 'Uganda' },
  { code: '+260', name: 'Zambia' },
  { code: '+263', name: 'Zimbabwe' },
  { code: '+351', name: 'Portugal' },
  { code: '+352', name: 'Luxembourg' },
  { code: '+353', name: 'Ireland' },
  { code: '+354', name: 'Iceland' },
  { code: '+358', name: 'Finland' },
  { code: '+359', name: 'Bulgaria' },
  { code: '+370', name: 'Lithuania' },
  { code: '+371', name: 'Latvia' },
  { code: '+372', name: 'Estonia' },
  { code: '+380', name: 'Ukraine' },
  { code: '+381', name: 'Serbia' },
  { code: '+385', name: 'Croatia' },
  { code: '+386', name: 'Slovenia' },
  { code: '+387', name: 'Bosnia & Herzegovina' },
  { code: '+420', name: 'Czech Republic' },
  { code: '+421', name: 'Slovakia' },
  { code: '+852', name: 'Hong Kong' },
  { code: '+853', name: 'Macau' },
  { code: '+855', name: 'Cambodia' },
  { code: '+856', name: 'Laos' },
  { code: '+880', name: 'Bangladesh' },
  { code: '+886', name: 'Taiwan' },
  { code: '+960', name: 'Maldives' },
  { code: '+961', name: 'Lebanon' },
  { code: '+962', name: 'Jordan' },
  { code: '+966', name: 'Saudi Arabia' },
  { code: '+968', name: 'Oman' },
  { code: '+971', name: 'UAE' },
  { code: '+972', name: 'Israel' },
  { code: '+973', name: 'Bahrain' },
  { code: '+974', name: 'Qatar' },
  { code: '+975', name: 'Bhutan' },
  { code: '+976', name: 'Mongolia' },
  { code: '+977', name: 'Nepal' },
  { code: '+992', name: 'Tajikistan' },
  { code: '+994', name: 'Azerbaijan' },
  { code: '+995', name: 'Georgia' },
  { code: '+996', name: 'Kyrgyzstan' },
  { code: '+998', name: 'Uzbekistan' },
]

interface Props {
  modelValue: string
}

const props = defineProps<Props>()
const emit = defineEmits<{ (e: 'update:modelValue', value: string): void }>()

const isOpen = ref(false)
const inputValue = ref(props.modelValue)
const activeIndex = ref(-1)
const inputRef = ref<HTMLInputElement | null>(null)

watch(() => props.modelValue, (v) => {
  if (v !== inputValue.value) inputValue.value = v
})

const filteredOptions = computed(() => {
  const q = inputValue.value.toLowerCase()
  if (!q) return COUNTRY_CODES
  return COUNTRY_CODES.filter(o =>
    o.code.includes(q) || o.name.toLowerCase().includes(q)
  )
})

function handleInput(e: Event) {
  const val = (e.target as HTMLInputElement).value
  inputValue.value = val
  isOpen.value = true
  activeIndex.value = -1
  emit('update:modelValue', val)
}

function handleFocus() {
  isOpen.value = true
  activeIndex.value = -1
}

function handleBlur() {
  setTimeout(() => {
    isOpen.value = false
    emit('update:modelValue', inputValue.value)
  }, 150)
}

function handleKeydown(e: KeyboardEvent) {
  if (!isOpen.value) {
    if (e.key === 'ArrowDown') { isOpen.value = true; return }
    return
  }
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    activeIndex.value = Math.min(activeIndex.value + 1, filteredOptions.value.length - 1)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    activeIndex.value = Math.max(activeIndex.value - 1, 0)
  } else if (e.key === 'Enter' && activeIndex.value >= 0) {
    e.preventDefault()
    selectOption(filteredOptions.value[activeIndex.value])
  } else if (e.key === 'Escape') {
    isOpen.value = false
  }
}

function selectOption(option: { code: string; name: string }) {
  inputValue.value = option.code
  emit('update:modelValue', option.code)
  isOpen.value = false
  activeIndex.value = -1
}
</script>
